import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '@context/shared/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { isAuthor, isAccreditor, canAccessAccreditation, resolveLandingPath } from '@config/shared/roles';

const ProtectedRoute = ({ children, requireAuth = true, requireAdmin = false }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Accreditor → confined to the accreditation overview. Even a known URL to
  // another page (admin or client) bounces them back here, not just links
  // they'd reach through the UI.
  const accreditorOutOfScope =
    !loading &&
    requireAuth &&
    isAuthenticated &&
    isAccreditor(user?.roleId) &&
    !location.pathname.startsWith('/accreditation');

  // Accreditation (overview + detail pages) → limited to admins, managers,
  // and accreditors. Everyone else is turned away, even with a known URL.
  const accreditationForbidden =
    !loading &&
    requireAuth &&
    isAuthenticated &&
    location.pathname.startsWith('/accreditation') &&
    !canAccessAccreditation(user?.roleId);

  // Hooks must run unconditionally, so the toast fires here rather than
  // inline with the redirects below.
  useEffect(() => {
    if (accreditorOutOfScope || accreditationForbidden) {
      // Stable id so a duplicate call (e.g. StrictMode's double effect
      // invocation in dev) replaces the existing toast instead of stacking
      // a second one.
      toast.error(t('error.forbiddenContent'), { id: 'protected-route-forbidden' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accreditorOutOfScope, accreditationForbidden]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated → redirect to login, save where they were trying to go
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  if (accreditorOutOfScope) {
    return <Navigate to="/accreditation" replace />;
  }

  if (accreditationForbidden) {
    return <Navigate to="/home" replace />;
  }

  // Below author level → redirect to client home
  if (requireAdmin && (!user || !isAuthor(user.roleId))) {
    return <Navigate to="/home" replace />;
  }

  // Authenticated user on auth page → send them where they were going
  if (!requireAuth && isAuthenticated) {
    const from = location.state?.from?.pathname;
    return <Navigate to={resolveLandingPath(user?.roleId, from)} replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node,
  requireAuth: PropTypes.bool,
  requireAdmin: PropTypes.bool,
};

export default ProtectedRoute;
